import { gql, useMutation } from '@apollo/client';
import { Button } from 'reactstrap';
import { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Form, Formik, FormikConfig } from 'formik';
import * as yup from 'yup';
import { useParams } from 'react-router';
import { captureEvent } from '../../../utils/analytics';
import { MapContext } from '../../../contexts/MapContext';
import { useQueryWithFarmId } from '../../../utils/hooks';
import { defaultMapZoom } from '../../../hooks/useMapState';
import AddressSearch from '../../public-suitability-tool/components/AddressSearch';
import { coordinatesSchema, coordsObjToPoint } from '../../../utils/geojson';
import { UpdateFarmMutation } from '../../../mutations';
import { useMapCenter } from '../../fields-preview/utils/map';
import { useFarmSettingsContext } from '../../../contexts/FarmSettingsContext';
import useGrowingZoneLookup from '../../../hooks/useGrowingZoneLookup';
import { growingZoneSchema } from '../../../constants/growingZones';
import { showError, showSuccess } from '../../../utils/toast';

const MapCenterQuery = gql`
  query MapCenterQuery($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      address
      growingZone
      mapCenter
      updatedAt
    }
  }
`;

const validationSchema = yup.object().shape({
  address: yup.string().nullable(),
  growingZone: growingZoneSchema,
  mapCenter: coordinatesSchema.required('Required'),
});

const MapCenterSearch = styled((props) => {
  const { farmId } = useParams();
  const {
    // @ts-expect-error MapContext not migrated to TS
    mapState: { mapLoaded, mapRef },
  } = useContext(MapContext);
  // Fn to look up growing zone from USDA data (via tileset)
  const lookUpGrowingZone = useGrowingZoneLookup();
  const [growingZoneEdited, setGrowingZoneEdited] = useState(false);

  const { data, loading } = useQueryWithFarmId(MapCenterQuery);
  const { openSettingsModalForFarm } = useFarmSettingsContext();
  const successMessage = (
    <span>
      Successfully updated map center
      {growingZoneEdited ? ' and growing zone' : ''}. You can edit{' '}
      {growingZoneEdited ? 'these values' : 'this value'} in{' '}
      <Button
        color="link"
        size="sm"
        className="p-0 align-baseline"
        onClick={() => openSettingsModalForFarm(farmId)}
      >
        project settings
      </Button>
      .
    </span>
  );
  const [updateFarm] = useMutation(UpdateFarmMutation, {
    onCompleted: ({ updateFarm: { result, errors } }) => {
      if (errors) {
        showError(errors);
      }
      if (result) {
        showSuccess(successMessage);
      }
    },
    onError: (e) => {
      showError(e);
    },
  });

  const onSubmit: FormikConfig<{
    address: string;
    growingZone: Nullable<string>;
    mapCenter: { lat: number; lng: number };
  }>['onSubmit'] = async (values) => {
    const castValues = validationSchema.cast(values);
    const mapCenter = coordsObjToPoint(castValues.mapCenter);
    if (!mapCenter) throw new Error('Invalid map center coordinates');
    let growingZone = null;
    try {
      growingZone = await lookUpGrowingZone(castValues.mapCenter);
      setGrowingZoneEdited(!values.growingZone && !!growingZone);
    } catch {
      // Some coordinates do not have a corresponding growingZone, so do nothing
    }
    await updateFarm({
      variables: {
        input: {
          ...castValues,
          // Keep the existing growingZone if one was already set
          growingZone: values.growingZone || growingZone,
          mapCenter,
          __typename: undefined,
        },
      },
    });
  };

  // Do not render if farm already has a mapCenter
  if (loading) return null;
  if (data?.farm?.mapCenter?.geometry) return null;

  // If the farm has an address but not a mapCenter (unlikely), do not display
  // address in search input
  const initialValues = { ...data?.farm, address: '' };
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ dirty, isSubmitting, setValues, values }) => {
        const [lng, lat] = useMapCenter(mapLoaded, mapRef);
        useEffect(
          () => setValues({ ...values, mapCenter: { lat, lng } }),
          // Only update the form values as user moves map
          // eslint-disable-next-line react-hooks/exhaustive-deps
          [lng, lat],
        );
        const submit = ({
          address,
          center,
        }: {
          address: string;
          center: { lat: number; lng: number };
        }) => {
          const map = mapRef.current;
          if (!map) return;

          captureEvent('map-center-search', { address, center });

          map.setCenter([center.lng, center.lat]);
          map.setZoom(defaultMapZoom);

          // Delay setting the address until the next tick so that the
          // address field isn't overwritten by the autofill.
          setTimeout(() =>
            setValues({
              ...values,
              address,
              mapCenter: center,
            }),
          );
        };

        return (
          <Form
            // Do not submit the form when 'Enter' key is pressed
            onKeyDown={(e) => e.code === 'Enter' && e.preventDefault()}
            {...props}
          >
            <div className="d-flex justify-content-between align-items-center mr-2">
              <AddressSearch
                className="mr-2"
                placeholder="(e.g. 123 Main St. or lat, lng)"
                submit={submit}
              />
              <Button
                disabled={!dirty || isSubmitting || loading}
                outline
                type="submit"
              >
                Set Map Center
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
})`
  input {
    font-size: 1em;
    width: 250px;
  }
  button {
    font-size: 1em;
  }
`;

export default MapCenterSearch;
